import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AdminLayout } from "./components/layout/AdminLayout";
import { Toaster } from "./components/ui/sonner";
import { Loader2, Lock } from "lucide-react";
import { useState } from "react";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import RegionsPage from "./pages/RegionsPage";
import CitiesPage from "./pages/CitiesPage";
import DistrictsPage from "./pages/DistrictsPage";
import CategoriesPage from "./pages/CategoriesPage";
import SubCategoriesPage from "./pages/SubCategoriesPage";
import ProjectsPage from "./pages/ProjectsPage";
import SuccessStoriesPage from "./pages/SuccessStoriesPage";
import OutletsPage from "./pages/OutletsPage";
import ImportCenterPage from "./pages/ImportCenterPage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import StatisticsDefinitionsPage from "./pages/StatisticsDefinitionsPage";
import RegionStatisticsPage from "./pages/RegionStatisticsPage";
import AliasesPage from "./pages/AliasesPage";
import MediaLibraryPage from "./pages/MediaLibraryPage";
import MapPage from "./pages/MapPage";
import ProjectPage from "./pages/ProjectPage";

/* ── Map Access Gate ── */
function MapGate() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('map_auth') === '1');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');

  if (authed) return <MapPage />;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (user === 'namaa20' && pass === 'N@maa2030') {
      sessionStorage.setItem('map_auth', '1');
      setAuthed(true);
    } else {
      setError('بيانات الدخول غير صحيحة');
      setTimeout(() => setError(''), 2500);
    }
  };

  return (
    <div data-testid="map-gate" style={{
      position: 'fixed', inset: 0,
      background: 'linear-gradient(170deg, #040a14 0%, #061826 50%, #0a2520 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Noto Kufi Arabic', 'Tajawal', sans-serif",
    }}>
      <form onSubmit={handleSubmit} data-testid="map-gate-form" style={{
        width: 340, padding: '36px 32px', borderRadius: 16,
        background: 'rgba(8, 16, 30, 0.85)', backdropFilter: 'blur(20px)',
        border: '1px solid rgba(47, 174, 143, 0.15)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column', gap: 16, direction: 'rtl',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <Lock size={28} style={{ color: '#2dd4bf', marginBottom: 8 }} />
          <h2 style={{ color: 'rgba(245,241,232,0.9)', fontSize: 18, fontWeight: 700, margin: 0 }}>
            الدخول إلى الخريطة
          </h2>
        </div>
        <input
          data-testid="map-gate-user"
          value={user} onChange={e => setUser(e.target.value)}
          placeholder="اسم المستخدم"
          autoComplete="username"
          style={{
            padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(47,174,143,0.2)',
            background: 'rgba(255,255,255,0.05)', color: '#f5f1e8', fontSize: 14,
            outline: 'none', fontFamily: 'inherit',
          }}
        />
        <input
          data-testid="map-gate-pass"
          type="password" value={pass} onChange={e => setPass(e.target.value)}
          placeholder="كلمة المرور"
          autoComplete="current-password"
          style={{
            padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(47,174,143,0.2)',
            background: 'rgba(255,255,255,0.05)', color: '#f5f1e8', fontSize: 14,
            outline: 'none', fontFamily: 'inherit',
          }}
        />
        <button data-testid="map-gate-submit" type="submit" style={{
          padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #2dd4bf 0%, #0d9488 100%)',
          color: '#fff', fontSize: 15, fontWeight: 700, fontFamily: 'inherit',
          transition: 'opacity 0.2s',
        }}>
          دخول
        </button>
        {error && <p data-testid="map-gate-error" style={{
          color: '#f87171', fontSize: 13, textAlign: 'center', margin: 0,
        }}>{error}</p>}
      </form>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F5F4]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public Route (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F5F4]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Map requires access credentials */}
      <Route path="/" element={<MapGate />} />
      <Route path="/project/:id" element={<ProjectPage />} />
      
      {/* Auth */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="regions" element={<RegionsPage />} />
        <Route path="cities" element={<CitiesPage />} />
        <Route path="districts" element={<DistrictsPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="subcategories" element={<SubCategoriesPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="success-stories" element={<SuccessStoriesPage />} />
        <Route path="outlets" element={<OutletsPage />} />
        <Route path="import" element={<ImportCenterPage />} />
        <Route path="import/history" element={<ImportCenterPage />} />
        <Route path="import/errors" element={<ImportCenterPage />} />
        <Route path="review-queue" element={<ReviewQueuePage />} />
        <Route path="statistics/definitions" element={<StatisticsDefinitionsPage />} />
        <Route path="statistics/values" element={<StatisticsDefinitionsPage />} />
        <Route path="statistics/region" element={<RegionStatisticsPage />} />
        <Route path="aliases" element={<AliasesPage />} />
        <Route path="media" element={<MediaLibraryPage />} />
        <Route path="map-categories" element={<CategoriesPage />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
